/**
 * Campo EVALUACION del catalogo: la evaluacion de desempeño que RRHH pedia
 * adjunta en las desvinculaciones (la planilla "Evaluación a Operarios").
 *
 * En EMSYS web se pinta como la planilla: una fila por concepto y una columna
 * por respuesta. En un telefono eso no entra, asi que cada concepto es una
 * tarjeta con las cuatro respuestas como botones y el comentario se abre solo
 * si hace falta.
 *
 * Los conceptos NO estan aca: salen de campo.evaluacion, que publica EMSYS.
 * El puntaje que se ve es orientativo; el que queda guardado lo calcula EMSYS.
 *
 * Tambien exporta EvaluacionResumen, que muestra una evaluacion ya guardada
 * (detalle de la solicitud y aprobacion del jefe).
 */
import {
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonNote,
  IonTextarea,
} from "@ionic/react";
import {
  chatbubbleEllipsesOutline,
  checkmarkCircle,
  clipboardOutline,
  refreshOutline,
} from "ionicons/icons";
import React, { useRef, useState } from "react";

import FirmaCanvas, { FirmaCanvasHandle } from "src/components/FirmaCanvas";
import {
  CampoCatalogo,
  EvaluacionSolicitud,
  ValorCampo,
  ValorEvaluacion,
  esValorEvaluacion,
  evaluacionVacia,
  resultadoEvaluacion,
} from "src/services/solicitudesRRHH";

/** Color de la clasificacion, igual que en EMSYS. */
const colorClasificacion = (codigo: string): string =>
  ({ BAJO: "danger", PROMEDIO: "warning", ALTO: "success" } as Record<string, string>)[
    codigo
  ] || "medium";

const vacio = (): ValorEvaluacion => ({ items: {}, textos: {} });

interface Props {
  campo: CampoCatalogo;
  valor: ValorCampo;
  requerido: boolean;
  error?: string;
  onCambio: (valor: ValorEvaluacion) => void;
}

const SolicitudRRHHEvaluacion: React.FC<Props> = ({
  campo,
  valor,
  requerido,
  error,
  onCambio,
}) => {
  const planilla = campo.evaluacion;
  const actual = esValorEvaluacion(valor) ? valor : vacio();
  // Opcional y sin empezar se muestra plegada: en una renuncia, por ejemplo,
  // 17 conceptos abiertos de entrada son mucho para algo que no se pide. Se
  // recalcula en cada render y no solo al montar: el motivo puede pasar a uno
  // que la exige, y al corregir una devuelta los valores llegan despues.
  const [abiertaAMano, setAbierta] = useState(false);
  const abierta = abiertaAMano || requerido || !evaluacionVacia(valor);
  // Conceptos con el comentario abierto (o con comentario escrito).
  const [conComentario, setConComentario] = useState<Record<string, boolean>>({});
  const firmaRef = useRef<FirmaCanvasHandle>(null);

  if (!planilla) return null;

  const resultado = resultadoEvaluacion(planilla, actual);
  const completa = resultado.respondidos === resultado.total;

  const responder = (codigo: string, puntaje: number) => {
    const previo = actual.items[codigo] || { puntaje: null, comentario: "" };
    onCambio({
      ...actual,
      items: { ...actual.items, [codigo]: { ...previo, puntaje } },
    });
  };

  const comentar = (codigo: string, comentario: string) => {
    const previo = actual.items[codigo] || { puntaje: null, comentario: "" };
    onCambio({
      ...actual,
      items: { ...actual.items, [codigo]: { ...previo, comentario } },
    });
  };

  const escribir = (codigo: string, texto: string) =>
    onCambio({ ...actual, textos: { ...actual.textos, [codigo]: texto } });

  // La firma se guarda en el valor al terminar cada trazo: asi no hace falta
  // un boton de "confirmar firma" y lo que viaja es siempre lo que se ve.
  const firmar = () => {
    // Recortada al trazo: sin el blanco de alrededor se lee bien en EMSYS.
    const png = firmaRef.current?.tieneTrazos()
      ? firmaRef.current.obtenerPngRecortado()
      : "";
    onCambio({ ...actual, firma: png });
  };

  const borrarFirma = () => {
    firmaRef.current?.limpiar();
    onCambio({ ...actual, firma: "" });
  };

  if (!abierta) {
    return (
      <div style={{ padding: "0 16px 8px" }}>
        <IonButton expand="block" fill="outline" onClick={() => setAbierta(true)}>
          <IonIcon icon={clipboardOutline} slot="start" />
          Completar evaluación (opcional)
        </IonButton>
      </div>
    );
  }

  return (
    <div style={{ padding: "0 12px" }}>
      {/* Avance y puntaje: arriba, porque es lo que se mira mientras se
          completa. */}
      <div
        style={{
          borderLeft: `4px solid var(--ion-color-${
            completa ? colorClasificacion(resultado.clasificacion) : "medium"
          })`,
          background: "var(--ion-color-light)",
          padding: "8px 12px",
          borderRadius: 4,
          marginBottom: 12,
        }}
      >
        {completa ? (
          <>
            Puntaje: <strong>{resultado.alcanzados} de {resultado.totales}</strong>{" "}
            ({resultado.porcentaje}%) ·{" "}
            <strong>{resultado.clasificacion_texto}</strong>
          </>
        ) : (
          <>
            Respondidos <strong>{resultado.respondidos}</strong> de{" "}
            {resultado.total} conceptos
          </>
        )}
        {error && (
          <IonNote color="danger" style={{ display: "block", marginTop: 4 }}>
            {error}
          </IonNote>
        )}
      </div>

      {planilla.secciones.map((seccion) => (
        <div key={seccion.codigo} style={{ marginBottom: 12 }}>
          <h3
            style={{
              fontSize: "0.95rem",
              fontWeight: 600,
              margin: "8px 4px",
              color: "var(--ion-color-primary)",
            }}
          >
            {seccion.titulo}
          </h3>
          {seccion.items.map((item) => {
            const respuesta = actual.items[item.codigo];
            const puntaje = respuesta?.puntaje || null;
            // Despues de un intento de envio, lo que falta se marca.
            const falta = !!error && !puntaje;
            const verComentario =
              conComentario[item.codigo] || !!(respuesta?.comentario || "").trim();
            return (
              <div
                key={item.codigo}
                style={{
                  border: `1px solid var(--ion-color-${falta ? "danger" : "light-shade"})`,
                  borderRadius: 8,
                  padding: "10px 10px 6px",
                  marginBottom: 8,
                }}
              >
                <div style={{ display: "flex", gap: 6 }}>
                  <div style={{ flex: 1, fontSize: "0.9rem" }}>
                    {item.descripcion ? (
                      <>
                        <strong>{item.titulo}</strong>
                        <div style={{ color: "var(--ion-color-medium)", fontSize: "0.8rem" }}>
                          {item.descripcion}
                        </div>
                      </>
                    ) : (
                      item.titulo
                    )}
                  </div>
                  {puntaje && (
                    <IonIcon icon={checkmarkCircle} color="success" style={{ fontSize: 20 }} />
                  )}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${planilla.escala.length}, 1fr)`,
                    gap: 4,
                    marginTop: 8,
                  }}
                >
                  {planilla.escala.map((opcion) => (
                    <IonButton
                      key={opcion.valor}
                      size="small"
                      fill={puntaje === opcion.valor ? "solid" : "outline"}
                      onClick={() => responder(item.codigo, opcion.valor)}
                      style={
                        {
                          margin: 0,
                          height: 40,
                          fontSize: "0.7rem",
                          textTransform: "none",
                          whiteSpace: "normal",
                          "--padding-start": "2px",
                          "--padding-end": "2px",
                        } as React.CSSProperties
                      }
                    >
                      {opcion.texto}
                    </IonButton>
                  ))}
                </div>
                {verComentario ? (
                  <IonTextarea
                    value={respuesta?.comentario || ""}
                    rows={2}
                    autoGrow
                    maxlength={500}
                    placeholder="Comentario"
                    onIonChange={(e) => comentar(item.codigo, e.detail.value || "")}
                    style={
                      {
                        marginTop: 6,
                        fontSize: "0.85rem",
                        background: "var(--ion-color-light)",
                        borderRadius: 4,
                        "--padding-start": "8px",
                      } as React.CSSProperties
                    }
                  />
                ) : (
                  <IonButton
                    fill="clear"
                    size="small"
                    onClick={() =>
                      setConComentario((prev) => ({ ...prev, [item.codigo]: true }))
                    }
                    style={{ margin: "4px 0 0", textTransform: "none" }}
                  >
                    <IonIcon icon={chatbubbleEllipsesOutline} slot="start" />
                    Agregar comentario
                  </IonButton>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {planilla.textos.map((texto) => (
        <IonItem key={texto.codigo} lines="full">
          <IonLabel position="stacked">
            {texto.etiqueta}
            {texto.requerido ? " *" : ""}
          </IonLabel>
          <IonTextarea
            value={actual.textos[texto.codigo] || ""}
            rows={3}
            autoGrow
            onIonChange={(e) => escribir(texto.codigo, e.detail.value || "")}
          />
        </IonItem>
      ))}

      {/* Firma del evaluador: es quien carga la solicitud. El evaluado no
          firma (ver la nota del catalogo en EMSYS). */}
      <div style={{ padding: "12px 4px 4px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <IonLabel color={error && !actual.firma ? "danger" : undefined}>
            <strong>Tu firma como evaluador *</strong>
          </IonLabel>
          <IonButton fill="clear" size="small" onClick={borrarFirma}>
            <IonIcon icon={refreshOutline} slot="start" />
            Borrar
          </IonButton>
        </div>
        <div
          style={{
            border: `1px solid var(--ion-color-${
              error && !actual.firma ? "danger" : "medium"
            })`,
            borderRadius: 8,
            overflow: "hidden",
            background: "#fff",
          }}
        >
          <FirmaCanvas ref={firmaRef} alto={150} onTrazo={firmar} />
        </div>
        <IonNote style={{ fontSize: "0.8rem" }}>
          Firmá con el dedo dentro del recuadro.
        </IonNote>
      </div>
    </div>
  );
};

/** Una evaluacion ya guardada: resultado, respuestas y textos. */
export const EvaluacionResumen: React.FC<{
  evaluacion: EvaluacionSolicitud;
  /** Sin el detalle por concepto (la tarjeta de aprobacion del jefe). */
  compacta?: boolean;
}> = ({ evaluacion, compacta }) => {
  const [verConceptos, setVerConceptos] = useState(!compacta);
  return (
    <>
      <div
        style={{
          borderLeft: `4px solid var(--ion-color-${colorClasificacion(
            evaluacion.clasificacion,
          )})`,
          background: "var(--ion-color-light)",
          padding: "8px 12px",
          borderRadius: 4,
          marginBottom: 8,
        }}
      >
        Puntaje:{" "}
        <strong>
          {evaluacion.puntos_alcanzados} de {evaluacion.puntos_totales}
        </strong>{" "}
        ({String(evaluacion.porcentaje).replace(".", ",")}%) ·{" "}
        <strong>{evaluacion.clasificacion_texto}</strong>
        <IonNote style={{ display: "block", fontSize: "0.8rem" }}>
          Evaluó {evaluacion.evaluador_nombre}
          {evaluacion.evaluador_cargo ? ` (${evaluacion.evaluador_cargo})` : ""} el{" "}
          {evaluacion.fecha_evaluacion}
        </IonNote>
      </div>

      {evaluacion.firma_evaluador && (
        <div style={{ marginBottom: 8 }}>
          <IonNote>Firma del evaluador</IonNote>
          <div>
            <img
              src={evaluacion.firma_evaluador}
              alt="Firma del evaluador"
              style={{
                width: "100%",
                maxWidth: 320,
                maxHeight: 140,
                objectFit: "contain",
                padding: 4,
                border: "1px solid var(--ion-color-light-shade)",
                borderRadius: 4,
                background: "#fff",
              }}
            />
          </div>
        </div>
      )}

      {evaluacion.textos
        .filter((t) => t.valor)
        .map((t) => (
          <div key={t.codigo} style={{ marginBottom: 8 }}>
            <IonNote>{t.etiqueta}</IonNote>
            <p style={{ margin: "2px 0 0", whiteSpace: "pre-line" }}>{t.valor}</p>
          </div>
        ))}

      {!verConceptos ? (
        <IonButton
          fill="clear"
          size="small"
          onClick={() => setVerConceptos(true)}
          style={{ textTransform: "none", margin: 0 }}
        >
          Ver respuestas por concepto
        </IonButton>
      ) : (
        evaluacion.secciones.map((seccion) => (
          <div key={seccion.codigo} style={{ marginTop: 8 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontWeight: 600,
                fontSize: "0.9rem",
              }}
            >
              <span>{seccion.titulo}</span>
              <span>
                {seccion.puntos_alcanzados}/{seccion.puntos_totales}
              </span>
            </div>
            {seccion.items.map((item) => (
              <div
                key={item.codigo}
                style={{
                  display: "flex",
                  gap: 8,
                  padding: "6px 0",
                  borderBottom: "1px solid var(--ion-color-light-shade)",
                  fontSize: "0.85rem",
                }}
              >
                <div style={{ flex: 1 }}>
                  {item.titulo}
                  {item.comentario && (
                    <IonNote style={{ display: "block", fontSize: "0.8rem" }}>
                      {item.comentario}
                    </IonNote>
                  )}
                </div>
                <strong style={{ whiteSpace: "nowrap" }}>{item.puntaje_texto}</strong>
              </div>
            ))}
          </div>
        ))
      )}
    </>
  );
};

export default SolicitudRRHHEvaluacion;
